import * as Backbone from "backbone-associations";
import { prop } from "../../decorators/property";
import { SearchCitationModel } from "./search-citation";
import { FulltextDatasourceModel } from "./fulltext-datasource";
import { Container } from "aurelia-dependency-injection";
import { AuthService } from "../../authentication/auth-service";
import { ContainerHelper } from "../../shared/helpers/container-helper";

export class ResultModel extends Backbone.AssociatedModel {

    @prop() public Summary: string;
    @prop() public Id: string; // DocKey
    @prop() public TruncatedSummary: string;
    @prop() public ShortSummary: string;
    @prop() public HasParallelCitations: boolean;
    @prop() public ParallelCitations: SearchCitationModel[];
    @prop() public HasNeutralCitations: boolean;
    @prop() public HasReportCitations: boolean;
    @prop() public NeutralCitations: SearchCitationModel[];
    @prop() public ReportCitations: SearchCitationModel[];
    @prop() public PositiveTreatments: number;
    @prop() public NeutralTreatments: number;
    @prop() public NegativeTreatments: number;
    @prop() public Title: string;
    @prop() public requestingLoad = false;
    @prop() public isWaypoint = false;
    @prop() public CelexNumber: string;
    @prop() public CellarId: string;
    @prop() public Court: string;
    @prop() public Reference: string;
    @prop() public ECLI: string;
    @prop() public DocTypeString: string;
    @prop() public ShowDownloadCheckbox = false;
    @prop() public SelectedForDownload = false;
    @prop() public JudgmentDate: Date;
    @prop() public TitleWidth = `${this.getPartOneWidth()}%`;
    @prop() public CourtWidth =  `${this.getPartTwoWidth()}%`;
    @prop() public ReferenceWidth =  `${this.getPartTwoWidth()}%`;
    @prop() public HasJudgmentDate: boolean = this.hasJudgmentDate();
    @prop() public FormattedDate: string = this.formatDate();
    @prop() public FulltextDatasources: FulltextDatasourceModel[];
    @prop() public ReportDatasources: FulltextDatasourceModel[];
    @prop() public IsProUser: boolean;

    constructor(options?) {
        super(options);
        const authService = ContainerHelper.AuthService;
        this.setCitationProperties();
        this.setCitationUrls();
        this.setReportUrl();
        this.IsProUser = authService.AppUser.Sku === "pro";
    }

    public updateDisplayProperties() {
        this.TitleWidth = `${this.getPartOneWidth()}%`;
        this.CourtWidth =  `${this.getPartTwoWidth()}%`;
        this.ReferenceWidth =  `${this.getPartTwoWidth()}%`;
    }

    public getPartOneWidth(): number {
        const maxChars = $("#results").width() / 920 * 110;
        const titleBias = maxChars / 10;
        const allowance = maxChars / 2;
        const yearAllowance = 5;
        const flagAllowance = 10;
        const parts = this.getParts();

        const partOneExceedsAllowance = (parts[0].length + yearAllowance) > allowance;
        const partTwoExceedsAllowance = (parts[1].length + flagAllowance) > allowance;
        let partOneAllowance;
        if ((partOneExceedsAllowance && partTwoExceedsAllowance)) {
            const partOneFullLength = parts[0].length + yearAllowance;
            partOneAllowance = allowance + Math.min(titleBias, partOneFullLength - allowance);
        } else if (!partOneExceedsAllowance && !partTwoExceedsAllowance) {
            partOneAllowance = allowance;
        } else if (partOneExceedsAllowance) {
            const partTwoCredit = (allowance - parts[1].length - flagAllowance);
            partOneAllowance = allowance + partTwoCredit;
            const partOneFullLength = parts[0].length + yearAllowance;
            partOneAllowance = Math.min(partOneFullLength, partOneAllowance);
        }
        else {
            const partOneCredit = (allowance - (parts[0].length + yearAllowance));
            let partTwoAllowance = allowance + partOneCredit;
            const partTwoFullLength = parts[1].length + flagAllowance;
            partTwoAllowance = Math.min(partTwoFullLength, partTwoAllowance);
            partOneAllowance = (maxChars - partTwoAllowance);
        }
        const width =  partOneAllowance / maxChars * 100;
        const value = Math.min(80, width) - (this.ShowDownloadCheckbox ? 10 : 0);
        return value;
    }

    public getPartTwoWidth() {
       return 100 - (this.getPartOneWidth() + (this.ShowDownloadCheckbox ? 10 : 0));
    }

    private setCitationProperties() {
        if (!this.ParallelCitations || !this.ParallelCitations.length) {
            this.HasNeutralCitations = false;
            this.HasReportCitations = false;
            this.HasParallelCitations = false;
        } else {
            this.NeutralCitations = _.filter(this.ParallelCitations, (citation) => {
                return citation.Source.IsNeutral;
            }).sort((a, b) => {
                return b.Source.Rank - a.Source.Rank;
            });
            this.ReportCitations = _.filter(this.ParallelCitations, (citation) => {
                return !citation.Source.IsNeutral;
            }).sort((a, b) => {
                return b.Source.Rank - a.Source.Rank;
            });
            this.HasNeutralCitations = this.NeutralCitations && this.NeutralCitations.length > 0;
            this.HasReportCitations = this.ReportCitations && this.ReportCitations.length > 0;
            this.HasParallelCitations = true;
        }
    }

    private setCitationUrls() {
        if (this.HasParallelCitations) {
            this.ReportCitations.forEach((citation) => {
                citation.Id = this.Id;
                citation.Url = this.buildReportCitationUrl(citation);
            });
            this.NeutralCitations.forEach((citation) => {
                citation.Id = this.Id;
                citation.Url = this.buildNeutralCitationUrl(citation.Id);
            });
        }
    }

    private buildNeutralCitationUrl(citationId: string) {
        const hasFullText = this.FulltextDatasources && this.FulltextDatasources.length > 0;
        return hasFullText ? "fulltext-judgment/" + citationId : "fulltext-sources/" + citationId;
    }

    private buildReportCitationUrl(citation: SearchCitationModel) {
        const spaceRegex = new RegExp(" ", "g");
        const reportNameForUrl = citation.Source.Name === null ? "overview" : "report-" + citation.Source.Name.trim().replace(spaceRegex, "-").toLowerCase();
        return reportNameForUrl + "/" + citation.Id;
    }

    private setReportUrl() {
        if (this.HasParallelCitations) {
            const titleForUrl = this.get("TitleForUrl");
            if (this.ReportDatasources) {
                this.ReportDatasources.forEach((report) => {
                    const citation = this.ReportCitations.find((c) => c.Source.Code === report.Code);
                    if (citation !== undefined) {
                        report.Url = this.buildReportUrl(titleForUrl, citation);
                        report.Name = citation.Source.Name;
                    }
                });
            }
        }
    }
    private buildReportUrl(caseTitle: string, citationModel: SearchCitationModel): string {
        if (citationModel === undefined) {
            return "";
        }
        const reportUrl = `case/${caseTitle}/${citationModel.Url}`;

        return reportUrl;
    }

    private getParts() {
        switch (this.DocTypeString) {
            case "CASE":
                return [this.Title, this.Court || ""];
            case "STATUTE":
            case "STATUTE_SECTION":
            case "SI":
               return [this.Title, this.Reference || ""];
            default:
                return [this.Title, this.CelexNumber || ""];
        }
    }
    private hasJudgmentDate(): boolean {
        this.ConvertJudgmentDateToDateType();
        return this.JudgmentDate && (this.JudgmentDate.getFullYear() !== 1);
    }

    private formatDate(): string {
        this.ConvertJudgmentDateToDateType();
        if (this.JudgmentDate) {
            return this.JudgmentDate.toDateString();
        }
        return "";
    }

    private ConvertJudgmentDateToDateType() {
        if (typeof this.JudgmentDate === "string") {
            this.JudgmentDate = new Date(this.JudgmentDate);
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/search/models/result.ts